/**
 * Best-effort client-side cache of `attachment URL -> original file name`. The backend stores each
 * receipt under a generated name (its own attachment id) and never echoes back the original name the
 * user picked, so once a File object leaves memory (page reload, edit reopened later) the app has
 * nothing else to show but that generated name. This cache is populated at upload time (see
 * `useSaveBill`) and consulted before falling back to the URL's own filename — a same-browser
 * mitigation only: a different browser/device still sees the generated name, since the real fix is a
 * backend field for the original name.
 */
const STORAGE_KEY = 'riloma:attachment-names';
const MAX_ENTRIES = 200;

function readMap(): Record<string, string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function writeMap(map: Record<string, string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // best-effort — storage full/unavailable (e.g. private browsing) is fine to ignore
  }
}

/** Record the original name the user picked for a receipt now stored at `url`. */
export function rememberAttachmentName(url: string | null | undefined, name: string): void {
  if (!url || !name) return;
  const map = readMap();
  map[url] = name;
  const keys = Object.keys(map);
  if (keys.length <= MAX_ENTRIES) {
    writeMap(map);
    return;
  }
  // Insertion order (oldest first) — rebuild with only the most recent MAX_ENTRIES kept (avoids
  // `delete` on a dynamically computed key, which eslint's no-dynamic-delete flags).
  const trimmed: Record<string, string> = {};
  for (const key of keys.slice(keys.length - MAX_ENTRIES)) {
    const value = map[key];
    if (value !== undefined) trimmed[key] = value;
  }
  writeMap(trimmed);
}

/** The original name previously remembered for `url`, or null when never recorded. */
export function recallAttachmentName(url: string | null | undefined): string | null {
  if (!url) return null;
  return readMap()[url] ?? null;
}
